import React, {createContext, useContext, useEffect, useState} from "react"
import AuthContext from "./AuthContext";

const ArchiveContext = createContext()
export default ArchiveContext

export const ArchiveProvider = ({children}) => {

    const [archiveItems, setArchiveItems] = useState([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const {authTokens, BASE_URL} = useContext(AuthContext)

    const getAllArchiveItems = async () => {
        setLoading(true)
        setError(null)
        
        try {
            const response = await fetch(`${BASE_URL}/api/get_archive_items/`, {
                method: 'GET',
                mode: "cors",
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            if (response.ok) {
                console.log("Archive items response ok")
                const responseData = await response.json()
                setArchiveItems(responseData)
            } else if (response.status === 404) {
                // 404 means no archive items exist yet, not an error
                console.log("No archive items found (404)")
                setArchiveItems([])
            } else {
                const errorData = await response.json()
                setError(errorData.message || `Failed to load archive items. Server responded with status ${response.status}`)
            }
        } catch (error) {
            console.log("Archive API error:", error)
            setError("Unable to connect to the server. Please check your internet connection and try again.")
        } finally {
            setLoading(false)
        }
    }

    const addArchiveItemToDB = async (title, persianTitle, description, persianDescription, startYear, endYear, imageFileName, imageFileType) => {
        console.log("Add archive item to db called")
        try {
            const response = await fetch(`${BASE_URL}/api/add_archive_item/`, {
                method: 'POST',
                mode: "cors",
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                    "Authorization": "Bearer " + String(authTokens?.access)
                },
                body: JSON.stringify({
                    title: title,
                    persian_title: persianTitle,
                    description: description,
                    persian_description: persianDescription,
                    start_year: startYear,
                    end_year: endYear || null,
                    image: {
                        filename: imageFileName,
                        filetype: imageFileType
                    },
                    document_count: 0
                }),
            });

            const responseData = await response.json()
            
            if (response.ok) {
                console.log("Archive item added to database successfully")
                return responseData
            } else {
                throw new Error(responseData.message || "Failed to add archive item to database")
            }
        } catch (error) {
            console.error('Add archive item error:', error);
            throw error
        }
    }

    const uploadToS3 = async (presignedUrl, file) => {
        const response = await fetch(presignedUrl, {
            method: "PUT",
            body: file,
            headers: {
                'Content-Type': file.type,
            },
        })
        
        if (response.ok) {
            console.log('Archive item image uploaded successfully');
        } else {
            console.error('Failed to upload archive item image:', response.statusText);
            throw new Error('Failed to upload image to S3')
        }
    }

    const addArchiveItem = async (newItem) => {
        try {
            // Extract image file information
            const imageFile = newItem.image
            const imageFileName = imageFile ? imageFile.name : 'placeholder.jpg'
            const imageFileType = imageFile ? imageFile.type : 'image/jpeg'

            // Add archive item to database and get presigned URL
            const responseData = await addArchiveItemToDB(
                newItem.title,
                newItem.persianTitle,
                newItem.description,
                newItem.persianDescription,
                newItem.yearStart,
                newItem.yearEnd,
                imageFileName,
                imageFileType
            )

            // Upload image to S3 if an image was provided
            if (imageFile && responseData.image_url) {
                await uploadToS3(responseData.image_url, imageFile)
            }

            // Refetch all archive items from the backend to get the updated list
            await getAllArchiveItems()
            
            return { success: true, message: "Archive item added successfully" }

        } catch (error) {
            console.log("Error adding archive item:", error)
            return { success: false, message: error.message || "Network error occurred while adding archive item" }
        }
    }

    const deleteArchiveItem = async (itemId) => {
        try {
            const response = await fetch(`${BASE_URL}/api/delete_archive_item/${itemId}/`, {
                method: "DELETE",
                mode: "cors",
                credentials: "include",
                headers: {
                    "Authorization": "Bearer " + String(authTokens?.access)
                }
            })
            
            if (response.ok) {
                const responseData = await response.json()
                // Remove the item from local state
                setArchiveItems(prev => prev.filter(item => item.id !== itemId))
                return { success: true, message: responseData.message || "Archive item deleted successfully" }
            } else {
                const errorData = await response.json()
                return { success: false, message: errorData.message || "Failed to delete archive item" }
            }
        } catch (error) {
            console.log("Error deleting archive item:", error)
            return { success: false, message: "Network error occurred while deleting archive item" }
        }
    }

    const retryLoadArchiveItems = () => {
        getAllArchiveItems()
    }

    // Load archive items when the context is first created
    useEffect(() => {
        getAllArchiveItems()
    }, [])

    let contextData = {
        archiveItems: archiveItems,
        loading: loading,
        error: error,
        getAllArchiveItems: getAllArchiveItems,
        addArchiveItem: addArchiveItem,
        deleteArchiveItem: deleteArchiveItem,
        retryLoadArchiveItems: retryLoadArchiveItems,
    }

    return(
        <ArchiveContext.Provider value={contextData}>
            {children}
        </ArchiveContext.Provider>
    )
} 